/*
 * Draft Room — live captain's draft + auto-balance recommendation.
 * Web port of DraftScreen. Polling kept light (every 4s while live)
 * to match the app's no-realtime-layer pattern.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Users, Play, Shuffle } from 'lucide-react';

import { api } from '@/lib/api';
import { useTournamentStore } from '@/store/tournamentStore';
import { AppShell } from '@/components/AppShell';
import type { DraftState, EventPlayer, TournamentEvent } from '@golf-app/shared';

export default function DraftRoom() {
  const { eventId } = useParams<{ eventId: string }>();
  const { activeEvent, setActiveEvent } = useTournamentStore();

  const [draft, setDraft] = useState<DraftState | null>(null);
  const [event, setEvent] = useState<TournamentEvent | null>(activeEvent?.id === eventId ? activeEvent : null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const load = useCallback(async () => {
    if (!eventId) return;
    try {
      const [d, ev] = await Promise.all([api.getDraftRoom(eventId), api.getTournament(eventId)]);
      setDraft(d); setEvent(ev); setActiveEvent(ev);
    } catch (e: any) {
      setError(e?.message ?? "Couldn't load draft");
    } finally {
      setLoading(false);
    }
  }, [eventId, setActiveEvent]);

  useEffect(() => { load(); }, [load]);

  // Light polling while live.
  useEffect(() => {
    if (timer.current) window.clearInterval(timer.current);
    if (draft?.room?.status === 'live') {
      timer.current = window.setInterval(load, 4000);
    }
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [draft?.room?.status, load]);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    try { await fn(); await load(); }
    catch (e: any) { setError(e?.message ?? 'Something went wrong'); }
    finally { setBusy(false); }
  }

  if (loading) {
    return (
      <AppShell back={{ to: `/app/tournaments/${eventId}`, label: 'Event' }}>
        <div className="t-loading"><div className="spinner" /></div>
      </AppShell>
    );
  }

  const isOwner = event?.myRole === 'owner';
  const room = draft?.room ?? null;
  const players = event?.players ?? [];
  const playerMap = new Map<string, EventPlayer>(players.map((p) => [p.id, p]));
  const teamMap = new Map((event?.teams ?? []).map((t) => [t.id, t]));
  const available = draft?.available ?? [];
  const picks = draft?.picks ?? [];
  const onClockEpId = room?.currentCaptainEventPlayerId ?? null;
  const onClockName = onClockEpId ? (playerMap.get(onClockEpId)?.displayName ?? 'Captain') : null;
  const onClockTeam = draft?.captains.find((c) => c.captainEpId === onClockEpId)?.teamId;

  const nm = (id: string) => playerMap.get(id)?.displayName ?? '—';

  function autoBalance() {
    if (!confirm('Auto-balance teams by handicap? You can still adjust afterward.')) return;
    run(() => api.autoBalanceTeams(eventId!, true));
  }

  return (
    <AppShell back={{ to: `/app/tournaments/${eventId}`, label: 'Event' }}>
      <section className="t-hero">
        <h1>Captain's Draft</h1>
        <p>Run a live snake draft on a real screen — or just auto-balance by handicap.</p>
      </section>

      {error && <div className="t-error">{error}</div>}

      {/* No draft yet, or not started */}
      {(!room || room.status === 'not_started') && (
        <div className="t-row" style={{ cursor: 'default', flexDirection: 'column', alignItems: 'stretch', padding: 18, gap: 14 }}>
          <p style={{ color: 'var(--text-sec)', margin: 0, fontSize: 14, lineHeight: 1.5 }}>
            Run a live snake draft (alternating captain picks), or auto-balance non-captain players
            across teams by handicap. You can still adjust manually afterward.
          </p>
          {isOwner && (
            <>
              {!room ? (
                <button className="t-btn t-btn-primary t-btn-block" disabled={busy} onClick={() => run(() => api.createDraftRoom(eventId!, { draftOrderType: 'snake' }))}>
                  <Users /> Create Draft
                </button>
              ) : (
                <button className="t-btn t-btn-primary t-btn-block" disabled={busy} onClick={() => run(() => api.startDraft(room.id))}>
                  <Play /> Start Draft
                </button>
              )}
              <button className="t-btn t-btn-secondary t-btn-block" disabled={busy} onClick={autoBalance}>
                <Shuffle /> Auto-Balance by Handicap
              </button>
            </>
          )}
        </div>
      )}

      {/* Live / paused / completed */}
      {room && room.status !== 'not_started' && (
        <>
          <div className="t-scoreboard" style={{ flexDirection: 'column', textAlign: 'center', gap: 8, borderColor: room.status === 'live' ? 'var(--green-bright)' : 'var(--border)' }}>
            <span className="t-section-label" style={{ margin: 0 }}>
              {room.status === 'completed' ? 'Draft complete' : room.status === 'paused' ? 'Paused' : 'On the clock'}
            </span>
            {room.status === 'live' && onClockName && (
              <div style={{ fontSize: 24, fontWeight: 900, color: 'var(--text)' }}>
                {onClockName}
                {onClockTeam && <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-sec)' }}> · {teamMap.get(onClockTeam ?? '')?.name ?? ''}</span>}
              </div>
            )}
            <div style={{ fontSize: 13, color: 'var(--green-bright)', fontWeight: 700 }}>Pick #{room.currentPickNumber}</div>
            {isOwner && room.status !== 'completed' && (
              <div className="t-actions" style={{ marginTop: 4, justifyContent: 'center' }}>
                {room.status === 'live'
                  ? <button className="t-btn t-btn-ghost" disabled={busy} onClick={() => run(() => api.pauseDraft(room.id))}>Pause</button>
                  : <button className="t-btn t-btn-ghost" disabled={busy} onClick={() => run(() => api.resumeDraft(room.id))}>Resume</button>}
                {picks.length > 0 && <button className="t-btn t-btn-ghost" disabled={busy} onClick={() => run(() => api.undoDraftPick(room.id))}>Undo</button>}
                <button className="t-btn t-btn-ghost" disabled={busy} onClick={() => run(() => api.finalizeDraft(room.id))}>Finish</button>
              </div>
            )}
          </div>

          {room.status === 'live' && (
            <>
              <div className="t-section-label">Available ({available.length})</div>
              {available.length === 0 ? (
                <div className="t-empty"><div className="t-empty-title">All players drafted.</div></div>
              ) : (
                <div className="t-list">
                  {available.map((p) => (
                    <div key={p.id} className="t-row" style={{ cursor: 'default' }}>
                      <div className="t-row-body">
                        <div className="t-row-name">{p.displayName}</div>
                        {p.handicap != null && <div className="t-row-sub">HCP {p.handicap}</div>}
                      </div>
                      {isOwner && (
                        <button className="t-btn t-btn-primary" style={{ padding: '6px 14px', fontSize: 13 }} disabled={busy} onClick={() => run(() => api.draftPick(room.id, p.id))}>
                          Draft
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {picks.length > 0 && (
            <>
              <div className="t-section-label">Picks</div>
              <div>
                {picks.map((pk) => {
                  const t = teamMap.get(pk.teamId ?? '');
                  return (
                    <div key={pk.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ width: 24, fontSize: 13, fontWeight: 800, color: 'var(--text-mut)' }}>{pk.pickNumber}</span>
                      <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{nm(pk.selectedEventPlayerId)}</span>
                      <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 5, background: t?.primaryColor ?? 'var(--text-mut)' }} />
                      <span style={{ fontSize: 12, color: 'var(--text-sec)', fontWeight: 700 }}>{t?.name ?? ''}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </AppShell>
  );
}
